import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { IQuestion } from 'cat-shared/utils'
import { AppModalDialog, AppGroup, AppButton } from 'client/ui-building-blocks'
import { Question } from 'client/components'

import {
  usePage,
  useQuestion,
  useModalDialogContext,
  usePageStateContext,
} from 'client/hooks'

import { type TGetQuestionsData } from 'client/api/useGetQuestions/useGetQuestions'

import { QuestionModalHeader } from './QuestionModalHeader'
import { useCallback } from 'react'

type Props = {
  questionsData?: TGetQuestionsData
  question: IQuestion | null
  open: boolean
  loading?: boolean
}

const QuestionModal = ({
  questionsData = { count: 0, data: [] },
  question = null,
  open,
  loading = false,
}: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const { isSubmitQuestionFormDirty } = usePageStateContext()
  const { navigateTo } = usePage()
  const { toggleQuestionModal, toggleSubmitQuestionModal } = useModalDialogContext()

  const [isFirstTimeOpened, setIsFirstTimeOpened] = useState<boolean>(true)

  const questionId = question?._id.toString() || null

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        setIsFirstTimeOpened(false)
      }, 250)
    } else {
      setIsFirstTimeOpened(true)
    }
  }, [open])

  const {
    data,
    isQuestionBeingFetched,
    isQuestionBeingSubmitted,
    handleQuestionSubmission,
  } = useQuestion(questionId)

  const questionData = data?.data?.[0] || null

  const onClose = () => {
    toggleQuestionModal(false)
    navigateTo({ path: pathname, preventScroll: true })
  }

  const onPrevClick = useCallback(() => {
    if (!isSubmitQuestionFormDirty) {
      document.getElementById('question-nav-prev')?.click()
    } else {
      toggleSubmitQuestionModal(true)
    }
  }, [isSubmitQuestionFormDirty])

  const onNextClick = useCallback(() => {
    if (!isSubmitQuestionFormDirty) {
      document.getElementById('question-nav-next')?.click()
    } else {
      toggleSubmitQuestionModal(true)
    }
  }, [isSubmitQuestionFormDirty])

  const renderQuestion = useMemo(() => {
    return (
      <Question
        question={{ ...questionData, ...question }}
        isBeingSubmitted={isQuestionBeingSubmitted}
        areResultsBeingLoaded={!questionData || isQuestionBeingFetched}
        handleQuestionSubmission={handleQuestionSubmission}
      />
    )
  }, [
    questionData,
    question,
    isQuestionBeingSubmitted,
    isQuestionBeingFetched,
    handleQuestionSubmission,
  ])

  const showNavBtns = questionsData?.count > 1

  return (
    <AppModalDialog
      open={open}
      loading={isFirstTimeOpened || loading}
      size="small"
      onClose={onClose}
      Header={
        <QuestionModalHeader
          questionsData={questionsData}
          question={{ ...questionData, ...question }}
          isDisabled={isQuestionBeingFetched || isQuestionBeingSubmitted}
        />
      }
      Content={renderQuestion}
      Actions={
        <AppGroup
          fullWidth="mobile"
          alignment="horizontal"
          position="right"
          gapSize="xxsmall"
        >
          <AppButton
            variant="outlined"
            text={t('common.buttons.close')}
            onClick={onClose}
          />
          {showNavBtns ? (
            <AppButton
              variant="success"
              text={t('common.buttons.prev')}
              onClick={onPrevClick}
              disabled={
                questionsData?.data.findIndex(q => q._id.toString() === questionId) === 0
              }
            />
          ) : null}
          {showNavBtns ? (
            <AppButton
              variant="success"
              text={t('common.buttons.next')}
              onClick={onNextClick}
              disabled={
                questionsData?.data.findIndex(q => q._id.toString() === questionId) ===
                questionsData?.data.length - 1
              }
            />
          ) : null}
        </AppGroup>
      }
    />
  )
}

export default QuestionModal
